import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import { useSessionValidation } from '../../../utils/sessionValidation';
import LogoLoader from '../../common/LogoLoader';
import Pagination from '../../common/Pagination';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, InputAdornment, Chip, Typography, Box, Button, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Snackbar, Alert,
  Select, MenuItem, FormControl
} from '@mui/material';
import { Search, Refresh, Close, InfoOutlined } from '@mui/icons-material';

/* ── Action colour map ─────────────────────────────────────────── */
const ACTION_COLORS = {
  CREATE:  { bg: '#dcfce7', color: '#15803d' },
  UPDATE:  { bg: '#dbeafe', color: '#1d4ed8' },
  DELETE:  { bg: '#fee2e2', color: '#dc2626' },
  LOGIN:   { bg: '#f3e8ff', color: '#7c3aed' },
  LOGOUT:  { bg: '#fef3c7', color: '#b45309' },
  VIEW:    { bg: '#e0f2fe', color: '#0369a1' },
  PRINT:   { bg: '#fce7f3', color: '#be185d' },
  EXPORT:  { bg: '#fff7ed', color: '#c2410c' },
  VERIFY:  { bg: '#ecfdf5', color: '#059669' },
  APPROVE: { bg: '#f0fdf4', color: '#16a34a' },
};

const getActionStyle = (action = '') => {
  return ACTION_COLORS[action.toUpperCase()] || { bg: '#f1f5f9', color: '#475569' };
};

/* ── Entity name prettifier ────────────────────────────────────── */
const prettifyEntity = (name = '') => {
  // If it's already a human-readable label (has spaces), just strip trailing "Api" if present
  if (name.includes(' ')) return name.replace(/\s*Api$/i, '').trim();
  // Otherwise it's a raw API name like "PurchaseOrderApi" — split on capitals
  return name.replace(/Api$/i, '').replace(/([A-Z])/g, ' $1').trim();
};

/* ── Date helpers ──────────────────────────────────────────────── */
const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
const firstOfMonth = () => {
  const t = new Date();
  return toYMD(new Date(t.getFullYear(), t.getMonth(), 1));
};

/* ══════════════════════════════════════════════════════════════════
   INSPECT VALUES MODAL
══════════════════════════════════════════════════════════════════ */
const InspectModal = ({ open, onClose, row }) => {
  if (!row) return null;

  const parse = (v) => {
    if (!v) return null;
    try { return JSON.parse(v); } catch { return v; }
  };

  const oldObj = parse(row.OldValues);
  const newObj = parse(row.NewValues);
  const action = (row.Action || '').toUpperCase();

  /* ── Collect all field keys from both objects ── */
  const allKeys = Array.from(new Set([
    ...(oldObj && typeof oldObj === 'object' ? Object.keys(oldObj) : []),
    ...(newObj && typeof newObj === 'object' ? Object.keys(newObj) : []),
  ]));

  /* ── Determine if a field changed ── */
  const isChanged = (key) => {
    if (!oldObj || !newObj) return false;
    return String(oldObj[key] ?? '') !== String(newObj[key] ?? '');
  };

  /* ── For UPDATE: only show keys that actually changed ── */
  const displayKeys = action === 'UPDATE'
    ? allKeys.filter(k => isChanged(k))
    : allKeys;

  /* ── Row highlight logic ──
       CREATE  → all rows green
       DELETE  → all rows red
       UPDATE  → changed rows yellow, unchanged normal
  ── */
  const getRowBg = (key) => {
    if (action === 'CREATE') return '#f0fdf4';
    if (action === 'DELETE') return '#fff1f2';
    if (action === 'UPDATE' && isChanged(key)) return '#fefce8';
    return null; // no highlight
  };

  const getChangedDot = (key) => {
    if (action === 'UPDATE' && isChanged(key))
      return <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#f59e0b', display: 'inline-block', marginRight: 5, flexShrink: 0 }} />;
    if (action === 'CREATE')
      return <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', display: 'inline-block', marginRight: 5, flexShrink: 0 }} />;
    if (action === 'DELETE')
      return <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#ef4444', display: 'inline-block', marginRight: 5, flexShrink: 0 }} />;
    return null;
  };

  /* ── Side-by-side diff table ── */
  const renderDiff = () => {
    if (displayKeys.length === 0 && action === 'UPDATE') return (
      <Typography sx={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', py: 2 }}>
        No field changes detected.
      </Typography>
    );

    if (displayKeys.length === 0) return (
      <Typography sx={{ color: '#9ca3af', fontSize: '13px', textAlign: 'center', py: 2 }}>
        No value details recorded for this action.
      </Typography>
    );

    const showOld = !!oldObj && action !== 'CREATE';
    const showNew = !!newObj && action !== 'DELETE';

    return (
      <Box sx={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
        {/* Column headers */}
        <Box sx={{ display: 'flex', bgcolor: '#f1f5f9', borderBottom: '1px solid #e5e7eb' }}>
          <Box sx={{ width: '30%', p: '5px 10px', fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Field</Box>
          {showOld && <Box sx={{ flex: 1, p: '5px 10px', fontSize: '11px', fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '0.5px', borderLeft: '1px solid #e5e7eb' }}>Before</Box>}
          {showNew && <Box sx={{ flex: 1, p: '5px 10px', fontSize: '11px', fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.5px', borderLeft: '1px solid #e5e7eb' }}>After</Box>}
        </Box>

        {displayKeys.map((key) => {
          const bg = getRowBg(key);
          const changed = action === 'UPDATE' && isChanged(key);
          const oldVal = oldObj?.[key];
          const newVal = newObj?.[key];

          return (
            <Box key={key} sx={{
              display: 'flex',
              borderBottom: '1px solid #f0f0f0',
              bgcolor: bg || 'transparent',
              '&:last-child': { borderBottom: 'none' },
            }}>
              {/* Field name */}
              <Box sx={{ width: '30%', p: '6px 10px', fontSize: '12px', fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center' }}>
                {getChangedDot(key)}
                <span style={{ wordBreak: 'break-word' }}>{key}</span>
                {changed && (
                  <Chip label="changed" size="small"
                    sx={{ ml: 'auto', fontSize: '0.55rem', height: 16, bgcolor: '#fef3c7', color: '#b45309', fontWeight: 700 }} />
                )}
              </Box>

              {/* Old value */}
              {showOld && (
                <Box sx={{
                  flex: 1, p: '6px 10px', fontSize: '12px', wordBreak: 'break-word',
                  borderLeft: '1px solid #e5e7eb',
                  color: changed ? '#dc2626' : '#6b7280',
                  textDecoration: changed ? 'line-through' : 'none',
                  fontStyle: oldVal === null || oldVal === undefined ? 'italic' : 'normal',
                }}>
                  {oldVal === null || oldVal === undefined
                    ? <em style={{ color: '#d1d5db' }}>null</em>
                    : String(oldVal)}
                </Box>
              )}

              {/* New value */}
              {showNew && (
                <Box sx={{
                  flex: 1, p: '6px 10px', fontSize: '12px', wordBreak: 'break-word',
                  borderLeft: '1px solid #e5e7eb',
                  color: changed ? '#16a34a' : '#1f2937',
                  fontWeight: changed ? 700 : 400,
                  fontStyle: newVal === null || newVal === undefined ? 'italic' : 'normal',
                }}>
                  {newVal === null || newVal === undefined
                    ? <em style={{ color: '#d1d5db' }}>null</em>
                    : String(newVal)}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  /* ── Changed fields summary badge ── */
  const changedCount = action === 'UPDATE' ? displayKeys.length : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
      <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '15px' }}>Inspect Values</Typography>
            {action === 'UPDATE' && changedCount > 0 && (
              <Chip label={`${changedCount} field${changedCount > 1 ? 's' : ''} changed`} size="small"
                sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#fef3c7', color: '#b45309', fontWeight: 700 }} />
            )}
            {action === 'CREATE' && (
              <Chip label="New Record" size="small"
                sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#dcfce7', color: '#15803d', fontWeight: 700 }} />
            )}
            {action === 'DELETE' && (
              <Chip label="Record Deleted" size="small"
                sx={{ fontSize: '0.65rem', height: 20, bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 700 }} />
            )}
          </Box>
          <Typography variant="caption" sx={{ color: '#6b7280' }}>
            {prettifyEntity(row.TableName)} — Record #{row.RecordId}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><Close fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Summary pills */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {[
            { label: 'Action', value: row.Action },
            { label: 'User',   value: row.Username || 'Unknown' },
            { label: 'IP',     value: row.IpAddress || '—' },
            { label: 'Time',   value: row.CreatedDate ? new Date(row.CreatedDate).toLocaleString('en-IN') : '—' },
          ].map(({ label, value }) => (
            <Box key={label} sx={{ bgcolor: '#f8f9fa', borderRadius: '6px', px: 1.5, py: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', fontSize: '10px' }}>{label}</Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '12px', color: '#1f2937' }}>{value}</Typography>
            </Box>
          ))}
        </Box>

        {/* Legend for UPDATE */}
        {action === 'UPDATE' && changedCount > 0 && (
          <Box sx={{ display: 'flex', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
            {[
              { dot: '#f59e0b', label: 'Changed field' },
              { text: 'strikethrough', color: '#dc2626', label: 'Old value' },
              { text: 'bold', color: '#16a34a', label: 'New value' },
            ].map(({ dot, text, color, label }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '11px', color: '#6b7280' }}>
                {dot && <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, display: 'inline-block' }} />}
                {text && <span style={{ color, textDecoration: text === 'strikethrough' ? 'line-through' : 'none', fontWeight: text === 'bold' ? 700 : 400, fontSize: 11 }}>abc</span>}
                <span>{label}</span>
              </Box>
            ))}
          </Box>
        )}

        {renderDiff()}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} size="small" sx={{ textTransform: 'none', color: '#6b7280' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

/* ══════════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════════ */
const AuditLogIndex = () => {
  useSessionValidation();
  const { companyId, branchId, token } = useAuth();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  /* filters */
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(toYMD(new Date()));
  const [actionFilter, setActionFilter] = useState('all');

  /* pagination */
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  /* inspect modal */
  const [inspectRow, setInspectRow] = useState(null);

  /* ── Fetch ─────────────────────────────────────────────────── */
  const fetchLogs = useCallback(async () => {
    if (!companyId || !token) return;
    setLoading(true);
    try {
      const res = await fetchData('AuditLogApi', `/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const data = res?.response_data || res || [];
      setLogs(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setError('Failed to load audit logs.');
    } finally {
      setLoading(false);
    }
  }, [companyId, branchId, token, fromDate, toDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  /* ── Filter ────────────────────────────────────────────────── */
  const filtered = logs.filter((log) => {
    const q = search.toLowerCase();
    const matchSearch =
      !q ||
      (log.Username || '').toLowerCase().includes(q) ||
      (log.Action || '').toLowerCase().includes(q) ||
      (log.TableName || '').toLowerCase().includes(q) ||
      String(log.RecordId || '').includes(q) ||
      (log.IpAddress || '').includes(q);
    const matchAction = actionFilter === 'all' || log.Action === actionFilter;
    return matchSearch && matchAction;
  });

  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginated = filtered.slice(startIndex, startIndex + rowsPerPage);

  const handlePageChange = (p) => setCurrentPage(p);
  const handleRowsPerPageChange = (v) => { setRowsPerPage(Number(v)); setCurrentPage(1); };

  /* ── Render ────────────────────────────────────────────────── */
  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <div style={{
        maxWidth: '1500px', margin: '0 auto', background: 'white',
        borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        padding: '12px', color: 'black', fontFamily: 'Times New Roman'
      }}>

        {/* ── Header ─────────────────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
          <h4 style={{ fontSize: '17px', fontWeight: 700, margin: 0, textTransform: 'uppercase', color: 'black', fontFamily: 'Times New Roman' }}>
            Audit Log
          </h4>
          <Tooltip title="Refresh" arrow>
            <IconButton size="small" onClick={fetchLogs} disabled={loading}
              sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: 29, width: 29 }}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* ── Filters ────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
          <TextField
            size="small" placeholder="Search user, action, entity, IP..."
            value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: 18 }} /></InputAdornment> }}
            sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: 29 } }}
          />

          <input type="date" value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }}
            style={{ height: 29, border: '1px solid #d1d5db', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: '#f8f9fa' }}
          />
          <input type="date" value={toDate}
            onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }}
            style={{ height: 29, border: '1px solid #d1d5db', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: '#f8f9fa' }}
          />

          <FormControl size="small">
            <Select value={actionFilter} onChange={(e) => { setActionFilter(e.target.value); setCurrentPage(1); }}
              sx={{ height: 29, fontSize: 12, borderRadius: '8px', backgroundColor: '#f8f9fa', minWidth: 130 }}>
              <MenuItem value="all">All Actions</MenuItem>
              {Object.keys(ACTION_COLORS).map(a => (
                <MenuItem key={a} value={a}>
                  <Chip label={a} size="small"
                    sx={{ fontSize: '0.65rem', height: 18, bgcolor: ACTION_COLORS[a].bg, color: ACTION_COLORS[a].color, fontWeight: 700 }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* ── Table ──────────────────────────────────────────── */}
        <div style={{ marginBottom: 10 }}>
          <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: 10, overflow: 'hidden' }}>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow style={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', height: 27 }}>
                  {['Sl No.', 'Timestamp', 'User', 'Role', 'Action', 'Affected Entity', 'Record ID', 'IP Address', 'Details'].map((h, i) => (
                    <TableCell key={h} style={{
                      color: 'white', fontWeight: 600, fontSize: 11,
                      textTransform: 'uppercase', letterSpacing: '0.5px',
                      padding: '4px 12px', height: 27,
                      textAlign: i >= 6 ? 'center' : 'left'
                    }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} style={{ textAlign: 'center', padding: 24, color: '#6b7280', fontSize: 13 }}>
                      {loading ? 'Loading...' : 'No audit logs found for the selected filters.'}
                    </TableCell>
                  </TableRow>
                ) : paginated.map((log, idx) => {
                  const style = getActionStyle(log.Action);
                  return (
                    <TableRow key={log.Id || idx} style={{ background: idx % 2 === 0 ? '#ffffff' : '#f8f9fa', borderBottom: '1px solid #f0f0f0' }}>
                      <TableCell style={{ fontSize: 12, padding: '6px 12px', color: '#6b7280' }}>{startIndex + idx + 1}</TableCell>
                      <TableCell style={{ fontSize: 12, padding: '6px 12px', color: '#374151', whiteSpace: 'nowrap' }}>
                        {log.CreatedDate ? new Date(log.CreatedDate).toLocaleString('en-IN', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit', hour12: false
                        }) : '—'}
                      </TableCell>
                      <TableCell style={{ fontSize: 12, padding: '6px 12px', color: '#1d4ed8', fontWeight: 600 }}>
                        {log.Username || 'Unknown'}
                      </TableCell>
                      <TableCell style={{ fontSize: 12, padding: '6px 12px', color: '#6b7280' }}>
                        {log.RoleName || 'N/A'}
                      </TableCell>
                      <TableCell style={{ padding: '6px 12px' }}>
                        <Chip label={log.Action || '—'} size="small"
                          sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700, bgcolor: style.bg, color: style.color }} />
                      </TableCell>
                      <TableCell style={{ fontSize: 12, padding: '6px 12px', color: '#374151' }}>
                        {prettifyEntity(log.TableName || '—')}
                      </TableCell>
                      <TableCell style={{ fontSize: 12, padding: '6px 12px', color: '#374151', textAlign: 'center' }}>
                        {log.RecordId || '—'}
                      </TableCell>
                      <TableCell style={{ fontSize: 12, padding: '6px 12px', color: '#6b7280', textAlign: 'center', fontFamily: 'monospace' }}>
                        {log.IpAddress || '—'}
                      </TableCell>
                      <TableCell style={{ padding: '6px 12px', textAlign: 'center' }}>
                        <Tooltip title="Inspect old & new values" arrow>
                          <Button size="small" onClick={() => setInspectRow(log)}
                            startIcon={<InfoOutlined sx={{ fontSize: 13 }} />}
                            sx={{ fontSize: 11, textTransform: 'none', color: '#2563eb', fontWeight: 600, minWidth: 0, p: '2px 6px' }}>
                            Inspect Values
                          </Button>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {/* ── Pagination ─────────────────────────────────────── */}
        <Pagination
          currentPage={currentPage} totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={rowsPerPage} onItemsPerPageChange={handleRowsPerPageChange}
          totalItems={filtered.length}
        />
      </div>

      {/* ── Inspect Modal ───────────────────────────────────── */}
      <InspectModal open={!!inspectRow} onClose={() => setInspectRow(null)} row={inspectRow} />

      {/* ── Loader ─────────────────────────────────────────── */}
      <LogoLoader open={loading} message="Loading audit logs..." />

      {/* ── Error snackbar ──────────────────────────────────── */}
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={() => setError('')} severity="error">{error}</Alert>
      </Snackbar>
    </div>
  );
};

export default AuditLogIndex;
